import type { NavItem } from "epubjs";

interface Props {
  items: NavItem[];
  onSelect: (href: string) => void;
  onClose: () => void;
  /** Render inside the bottom bar's panel slot instead of as a
   *  floating overlay: no fixed positioning, no scrim, inherits the
   *  bar's background. 2026-10-06 — Thomas asked for panels to sit on
   *  top of the progress row as part of the bar assembly. */
  inline?: boolean;
}

export function TOCPanel({ items, onSelect, onClose, inline }: Props) {
  return (
    <>
      {/* Click-outside scrim. Closes the panel on tap anywhere else. */}
      {!inline && (
      <div
        className="fixed inset-0 z-20 bg-black/20"
        onClick={onClose}
        aria-hidden
      />
      )}
      {/* Centred modal sized to ~70vh — fits comfortably on a phone in
          landscape or portrait, scrolls internally for long TOCs. */}
      <div
        className={inline ? "pv-fg flex min-h-0 w-full flex-col overflow-y-auto" : "pv-surface pv-border pv-fg fixed inset-x-0 z-30 mx-auto flex max-h-[60vh] w-[88vw] max-w-md flex-col rounded-lg border"}
        /* 2026-10-06: was centred on screen; now anchored above the bar. */
        style={inline ? {} : { bottom: "calc(var(--pv-bar-h, 152px) + 8px)" }}
      >
        <div className="pv-border flex items-center justify-between border-b px-4 py-3">
          <div className="text-base font-semibold">Contents</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="pv-fg flex items-center justify-center"
            style={{ minHeight: "48px", minWidth: "48px", background: "none", border: "none", fontSize: "22px", lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
        {items.length === 0 ? (
          <div className="pv-fg-soft p-6 text-center text-sm">
            This book has no table of contents.
          </div>
        ) : (
          <ul className="flex-1 overflow-y-auto p-2 text-base">
            {flatten(items).map((it, i) => (
              <li key={`${it.href}-${i}`}>
                <button
                  type="button"
                  onClick={(e) => {
                    // Stop the click bubble so the scrim doesn't dismiss
                    // the panel before navigation has a chance to start.
                    e.stopPropagation();
                    onSelect(it.href);
                  }}
                  className="hover:bg-white/5 block w-full truncate rounded px-2 py-2 text-left"
                  style={{ paddingLeft: `${(it.depth ?? 0) * 12 + 8}px` }}
                >
                  {it.label.trim()}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

interface FlatItem {
  label: string;
  href: string;
  depth: number;
}

function flatten(items: NavItem[], depth = 0, acc: FlatItem[] = []): FlatItem[] {
  for (const it of items) {
    acc.push({ label: it.label, href: it.href, depth });
    if (it.subitems && it.subitems.length) flatten(it.subitems, depth + 1, acc);
  }
  return acc;
}
